import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import './Herramientas.css';

const HOMES_ORIGIN = config.backendUrl.replace(/\/$/, '');

/** Extrae `userId` del payload JWT (sin verificar firma; solo para armar URL de SSO). */
function parseJwtUserId(jwt) {
  if (!jwt || typeof jwt !== 'string') return null;
  const parts = jwt.split('.');
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const payload = JSON.parse(atob(b64));
    const id = payload.userId ?? payload.sub;
    if (id == null) return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Abre otra app del mismo origen con `token` y `userId` en query cuando hay sesión.
 * Importante: el token puede existir antes de que `user` esté hidratado en React.
 */
function openExternalAppWithJwt(pathname, authToken, userIdFromSession) {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const url = new URL(path, `${HOMES_ORIGIN}/`);
  if (authToken) {
    url.searchParams.set('token', authToken);
    const uid = userIdFromSession ?? parseJwtUserId(authToken);
    if (uid != null) url.searchParams.set('userId', String(uid));
  }
  window.open(url.toString(), '_blank', 'noopener,noreferrer');
}

const Herramientas = () => {
  const { user, token } = useAuth();
  
  const handleMetradosClick = () => {
    // Abrir en nueva pestaña
    window.open('https://www.homesconstructora.com/metrados', '_blank');
  };

  const handleEMSClick = () => {
    // Abrir en nueva pestaña
    window.open('https://www.homesconstructora.com/ems/', '_blank');
  };

  const handleBoletasClick = () => {
    // Abrir en nueva pestaña con HTTPS para compartir localStorage
    window.open('https://www.homesconstructora.com/sistema-boletas', '_blank');
  };

  const handleProformasClick = () => {
    // Abrir en nueva pestaña
    window.open('https://www.homesconstructora.com/sistema-proformas', '_blank');
  };

  const handleAdminObrasClick = () => {
    // Abrir en nueva pestaña
    window.open('https://www.homesconstructora.com/admin-obras', '_blank');
  };

  const handleContratosConstruccionClick = () => {
    window.open('https://www.homesconstructora.com/contratos-construccion', '_blank');
  };

  const handleCostosFijosClick = () => {
    // Abrir en nueva pestaña
    window.open('https://www.homesconstructora.com/costos-fijos', '_blank');
  };

  const handleEgresosIngresosClick = () => {
    // Abrir en nueva pestaña con HTTPS para compartir localStorage
    window.open('https://www.homesconstructora.com/egresos-ingresos', '_blank');
  };

  const handleSubdivisionWebClick = () => {
    // Abrir en nueva pestaña
    window.open('https://www.homesconstructora.com/subdivision-web', '_blank');
  };

  const handleCatalogoClick = () => {
    // Abrir en nueva pestaña
    window.open('https://www.homesconstructora.com/catalogo-homes', '_blank');
  };

  const handleAsistenciaClick = () => {
    const authToken = token || localStorage.getItem('token');
    openExternalAppWithJwt('/asistencia-homes', authToken, user?.id ?? null);
  };

  const handleAgendaContactosClick = () => {
    // Abrir en nueva pestaña
    window.open('https://www.homesconstructora.com/agenda-contactos', '_blank');
  };

  const handleOcupacionEmpleadosClick = () => {
    const authToken = token || localStorage.getItem('token');
    openExternalAppWithJwt('/ocupacion-empleados', authToken, user?.id ?? null);
  };

  const handleBibliotecaClick = () => {
    const authToken = token || localStorage.getItem('token');
    openExternalAppWithJwt('/documentos-importantes/', authToken, user?.id ?? null);
  };

  const handleWhatsAppClick = () => {
    const authToken = token || localStorage.getItem('token');
    if (!authToken) {
      alert('No hay sesión activa. Inicia sesión nuevamente.');
      return;
    }
    openExternalAppWithJwt('/homes-whatsapp/', authToken, user?.id ?? null);
  };

  return (
    <div className="herramientas-container">
      <div className="herramientas-grid">
        <div className="herramienta-card" onClick={handleMetradosClick}>
          <div className="herramienta-icon">
            📊
          </div>
          <div className="herramienta-content">
            <h3>Metrados</h3>
            <p>Sistema de cálculo y metrados de construcción</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleEMSClick}>
          <div className="herramienta-icon">
            🏗️
          </div>
          <div className="herramienta-content">
            <h3>Estudios de Suelo</h3>
            <p>Sistema de gestión de estudios de suelos y análisis geotécnicos</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleBoletasClick}>
          <div className="herramienta-icon">
            🎫
          </div>
          <div className="herramienta-content">
            <h3>Sistema de Boletas</h3>
            <p>Gestión de boletas y facturación del sistema</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleProformasClick}>
          <div className="herramienta-icon">
            📋
          </div>
          <div className="herramienta-content">
            <h3>Sistema de Proformas</h3>
            <p>Generación de cotizaciones y proformas para clientes</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleAdminObrasClick}>
          <div className="herramienta-icon">
            🏢
          </div>
          <div className="herramienta-content">
            <h3>Administración de Obras</h3>
            <p>Sistema completo de gestión de obras: proyectos, abonos, gastos, planos y reportes</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleContratosConstruccionClick}>
          <div className="herramienta-icon">
            📝
          </div>
          <div className="herramienta-content">
            <h3>Contratos de Construcción</h3>
            <p>Generación de contratos a todo costo, mano de obra y supervisión; cronogramas de pago y PDF corporativo.</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        {user && user.rol === 'JEFE' && (
          <div className="herramienta-card" onClick={handleCostosFijosClick}>
            <div className="herramienta-icon">
              💳
            </div>
            <div className="herramienta-content">
              <h3>Costos Fijos</h3>
              <p>Gestión de pagos recurrentes: alquiler, servicios, gastos fijos con calendario y evidencias</p>
              <div className="herramienta-badge">
                Externa
              </div>
            </div>
            <div className="herramienta-arrow">
              →
            </div>
          </div>
        )}

        {user && user.rol === 'JEFE' && (
          <div className="herramienta-card" onClick={handleWhatsAppClick}>
            <div className="herramienta-icon">
              💬
            </div>
            <div className="herramienta-content">
              <h3>Automatización WhatsApp</h3>
              <p>Panel de chats, bot comercial y gestión de clientes por WhatsApp</p>
              <div className="herramienta-badge">
                Externa
              </div>
            </div>
            <div className="herramienta-arrow">
              →
            </div>
          </div>
        )}

        <div className="herramienta-card" onClick={handleEgresosIngresosClick}>
          <div className="herramienta-icon">
            💰
          </div>
          <div className="herramienta-content">
            <h3>Gestor de Egresos e Ingresos</h3>
            <p>Sistema integrado de control financiero: abonos, boletas, costos fijos y movimientos en un solo lugar</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleSubdivisionWebClick}>
          <div className="herramienta-icon">
            📐
          </div>
          <div className="herramienta-content">
            <h3>Subdivisión Web</h3>
            <p>Gestión de expedientes de subdivisión y acumulación con generación automática de documentos</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleCatalogoClick}>
          <div className="herramienta-icon">
            📚
          </div>
          <div className="herramienta-content">
            <h3>Catálogo de Servicios</h3>
            <p>Catálogo digital de servicios profesionales con administración de contenido</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleAsistenciaClick}>
          <div className="herramienta-icon">
            ⏰
          </div>
          <div className="herramienta-content">
            <h3>Asistencia</h3>
            <p>Registro de entrada y salida con geolocalización</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleAgendaContactosClick}>
          <div className="herramienta-icon">
            📇
          </div>
          <div className="herramienta-content">
            <h3>Agenda de Contactos</h3>
            <p>Gestión de contactos profesionales y proveedores de la empresa</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        <div className="herramienta-card" onClick={handleBibliotecaClick}>
          <div className="herramienta-icon">
            📖
          </div>
          <div className="herramienta-content">
            <h3>Biblioteca</h3>
            <p>Gestión de documentos, manuales y recursos de la empresa</p>
          </div>
          <div className="herramienta-arrow">
            →
          </div>
        </div>

        {(user && (user.rol === 'ADMIN' || user.rol === 'JEFE')) && (
          <div className="herramienta-card" onClick={handleOcupacionEmpleadosClick}>
            <div className="herramienta-icon">
              👥
            </div>
            <div className="herramienta-content">
              <h3>Ocupación de Empleados</h3>
              <p>Gestión y seguimiento de la ocupación y asignación de empleados</p>
              <div className="herramienta-badge">
                Externa
              </div>
            </div>
            <div className="herramienta-arrow">
              →
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Herramientas;
